import { PropsWithChildren } from "react";
import { Switch } from "@fluentui/react-components";
import { SearchTextBox } from "../Primitives/TextBox";
import TrashIcon from "../../../assets/svg/remove.svg";
import type { TFolder } from "../../models/folder-model";
import WarningIcon from "../../../assets/svg/warning.svg";
import { IFolderSelectedFileCount } from "./AttachFilesContainer";
import { FilesGridContainer } from "../FilesGrid/FilesGridContainer";
import { TFile, getSizeMany, getPrettySizeFromBytes, MB_SIZE } from "../../models/file-model";
import { PrimaryButton, SecondaryButton, SecondarySubtleButton } from "../Primitives/Buttons";
import { FolderExplorerContainer } from "../FolderExplorer/FolderExplorerContainer";
import { TFolderService } from "../../services/draft-attachments-service";
import { TFilesRetriever } from "../../services/files-retriever";

export const isFile = (thing: TFolder | TFile): thing is TFile => thing.type === "FILE";
export const isFolder = (thing: TFolder | TFile): thing is TFolder => thing.type === "FOLDER";

export type TViewType = "SEARCH" | "FOLDER";

type Props = PropsWithChildren<{
  selected: TFile[];
  matterId: string;
  viewType: TViewType;
  showEmails: boolean;
  isSearching: boolean;
  emptyMessage: string;
  searchInProgress: boolean;
  fileLoadInProgress: boolean;
  folderService: TFolderService;
  filesRetriever: TFilesRetriever;
  files: (TFile | TFolder)[] | null;
  folderAndSelectedFileCounts: IFolderSelectedFileCount;

  onCancel: () => void;
  onAttach: () => void;
  clearSelected: () => void;
  toggleShowEmails: () => void;
  onSelectFolder: (id: string) => void;
  onSelectFile: (target: TFile) => void;
  searchForFilesAsync: (text: string) => Promise<TFile[]>;
}>;

export const AttachFilesPresentational = (props: Props) => {
  const size = getSizeMany(props.selected);

  const minSearchLength = 3;
  const maxSize = 20 * MB_SIZE;
  const maxSizeExceeded = size >= maxSize;
  const prettySize = getPrettySizeFromBytes(getSizeMany(props.selected));

  return (
    <div className="flex flex-col flex-grow min-h-0 h-full">
      <h1 className="text-2xl tracking-wide leading-none mb-4">Select Files</h1>
      {/* meat and potatoes container */}
      <div className="flex flex-grow flex-row bg-white items-center min-h-0 oveflow-auto max-w-full">
        <div className="flex flex-col w-[280px] self-start overflow-auto max-h-full h-full max-w-full border border-neutral-150 rounded-l">
          {maxSizeExceeded && (
            <div className="flex flex-row max-w-full justify-start items-center px-4 py-3 border-b h-12 min-h-0 max-h-12 border-neutral-150 bg-descriptive-yellow-150">
              <img src={WarningIcon} title="warning" className="w-7 h-7 mr-2" />
              <span>Attachments 20MB Maximum</span>
            </div>
          )}
          <div className="flex flex-row max-w-full h-12 justify-start items-center px-2 py-2 border-b border-neutral-150">
            <Switch checked={props.showEmails} onChange={props.toggleShowEmails} />
            <div>Show Emails</div>
          </div>

          {props.viewType === "SEARCH" && (
            <div className="m-4 justify-center justify-items-center">{`Selected ${props.selected.length} / ${
              props.files?.length ?? 0
            } (${prettySize}) search results`}</div>
          )}

          {props.viewType === "FOLDER" && (
            <FolderExplorerContainer
              selectMode="NONE"
              allowCreate={false}
              allowDelete={false}
              allowRename={false}
              matterId={props.matterId}
              folderService={props.folderService}
              filesRetriever={props.filesRetriever}
              folderAndSelectedFileCounts={props.folderAndSelectedFileCounts}
              onFolderSelected={props.onSelectFolder}
            />
          )}
        </div>
        {/* RHS container with file info, search, and list in it */}
        <div className="flex flex-1 flex-col border border-l-0 max-h-full min-h-full max-w-full rounded-r border-neutral-150 self-start overflow-hidden">
          <div className="flex flex-row bg-neutral-50 text-sm py-1 px-3 h-12 max-h-12 min-h-12 items-center justify-between border-b border-neutral-150">
            {/* Align this paragraph to the left */}
            <p className="flex-initial">
              {(props.selected || []).length} files selected ({prettySize})
            </p>

            {/* Align these elements to the right */}
            <div className="flex flex-row items-center ml-auto space-x-2">
              {/* Conditional rendering for clear button */}
              {(props.selected || []).length > 0 && (
                <SecondaryButton leftIcon={TrashIcon} onClick={props.clearSelected}>
                  Clear Selected
                </SecondaryButton>
              )}

              <div className="relative flex items-center">
                <SearchTextBox
                  throttleMs={600}
                  minLength={minSearchLength}
                  isLoading={props.isSearching}
                  placeholder="Search for files..."
                  onSearchAsync={props.searchForFilesAsync}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-1 min-h-0 flex-grow overflow-auto">
            <FilesGridContainer
              onSelectFile={props.onSelectFile}
              selectedFileIds={(props.selected || []).map((f) => f.id)}
              isLoadingFiles={props.searchInProgress || props.fileLoadInProgress}
              files={(props.files?.filter((file) => isFile(file)) as TFile[]) ?? []}
            />
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-row justify-end">
        <SecondarySubtleButton title="Cancel" onClick={props.onCancel}>
          Cancel
        </SecondarySubtleButton>
        <PrimaryButton className="ml-4" disabled={maxSizeExceeded} onClick={props.onAttach}>
          Attach Files
        </PrimaryButton>
      </div>
    </div>
  );
};
